export default {
    title: 'Notes',
    subtitle: '{n} note | {n} notes',
    newNote: 'New note',
    search: 'Search notes',
    searchPlaceholder: 'Search notes…',
    filters: {
        title: 'Filters',
        pinned: 'Pinned only',
        select: 'Select multiple',
    },
    sort: {
        title: 'Sort: {sort}',
        newest: 'Newest',
        oldest: 'Oldest',
        alpha: 'Alphabetical (A–Z)',
        short: {
            newest: 'Newest',
            oldest: 'Oldest',
            alpha: 'A–Z',
        },
    },
    capture: {
        placeholder: 'Jot something down…',
        hint: 'Enter saves · Shift+Enter new line · the first line is the title',
        save: 'Save',
    },
    list: {
        pinned: 'Pinned',
        untitled: 'Untitled',
        yesterday: 'yesterday',
        sections: '{done} of {total}',
        links: '{n} link | {n} links',
    },
    empty: {
        noMatchTitle: 'Nothing matches your search',
        noMatchText: 'Change the phrase or clear the search field.',
        noPinnedTitle: 'No pinned notes',
        noPinnedText: 'Pin a note with the pin on its bar, or turn off the “Pinned only” filter.',
        title: 'Your notebook is empty',
        text: 'Type something in the field above — a gate code, books to read, songs with links.',
    },
    categories: {
        title: 'Categories',
        all: 'All',
        none: 'No category',
        add: 'New category',
        pick: 'Category',
        edit: 'Edit category “{name}”',
        name: 'Category name',
        namePlaceholder: 'e.g. Music, Books, Home',
        color: 'Colour',
        save: 'Save',
        cancel: 'Cancel',
        remove: 'Delete',
        removed: 'Category “{name}” deleted — its notes stayed',
    },
    archive: {
        title: 'Archive',
        back: 'Back to notes',
        open: 'Archive ({n})',
        empty: 'The archive is empty',
    },
    pick: {
        title: 'Pick a note',
        text: 'Or start a new one — with the “New note” button or in the “Jot something down…” field above the list.',
    },
    toolbar: {
        back: 'Back to the list',
        pin: 'Pin',
        unpin: 'Unpin',
        archive: 'Archive',
        restore: 'Restore from archive',
        remove: 'Delete',
        inArchive: 'In the archive',
        saving: 'Saving…',
        saveError: 'Couldn’t save — we’ll try again with your next change',
        edited: 'Edited {when}',
    },
    toast: {
        removed: 'Note deleted',
        archived: 'Moved to the archive',
        restored: 'Restored from the archive',
        undo: 'Undo',
    },
    select: {
        toolbar: 'Selected notes',
        count: '{n} selected',
        all: 'Select all visible',
        none: 'Deselect all',
        move: 'Move to category',
        archive: 'Archive',
        restore: 'Restore',
        remove: 'Delete',
        cancel: 'Cancel',
        moved: '{n} note moved to “{name}” | {n} notes moved to “{name}”',
        archived: '{n} note archived | {n} notes archived',
        restored: '{n} note restored from the archive | {n} notes restored from the archive',
        confirmTitle: 'Delete {n} note? | Delete {n} notes?',
        removed: '{n} note deleted | {n} notes deleted',
    },
    editor: {
        title: 'Title',
        titlePlaceholder: 'Title',
        content: 'Note content',
        placeholder: 'Write… “## ” makes a heading, “- [ ] ” a checkbox, a link pasted on an empty line becomes a button.',
        heading: 'Heading',
        section: 'Section to tick off',
        bullet: 'List',
        task: 'Checkbox',
        link: 'Link',
        done: 'Done',
        hint: 'Esc finishes editing. Saves automatically.',
    },
    reader: {
        empty: 'Empty note — click to write.',
        collapse: 'Collapse section',
        expand: 'Expand section',
    },
    toc: {
        title: 'Contents',
        search: 'Search contents',
        noMatch: 'No heading matches.',
        done: '{done} of {total} ticked off',
    },
    help: {
        capture: {
            title: 'Jot something down',
            text: 'The quickest way to a note: type and press Enter. The first line becomes the title, the rest the content.',
            more: 'Shift+Enter adds a new line. The note goes to the category you are looking at. Start a longer note with the “New note” button.',
        },
        categories: {
            title: 'Categories',
            text: 'Order instead of one long list: Music, Books, Home… Pick a category to see only its notes.',
            more: 'Add a new category with the plus next to the heading. A note is in at most one category — set it on the bar of the open note. Rename, colour and delete are under “…” next to the category (on a phone: tap the selected pill again). Deleting a category keeps its notes.',
        },
        list: {
            title: 'Your notes',
            text: 'Pinned ones are on top, the rest in the order chosen next to search (by last change by default). Search by title and content.',
            more: 'Each note shows its category, ticked sections, checkboxes and links. The sliders icon holds the filters: “Pinned only”, “Archive” (notes you don’t come back to — they aren’t lost and the list gets shorter) and “Select multiple” to move, archive or delete several at once (on a computer also Ctrl/Cmd+click, Shift+click selects a range; on a phone, press and hold a note).',
        },
        note: {
            title: 'A note',
            text: 'Click the text to write — it saves itself. “## ” makes a heading, “- [ ] ” a checkbox, and a link pasted on an empty line becomes a button.',
            more: 'A heading with a checkbox (“## [ ] Dune”) is a section to tick off — a book read, a song learned. With 3 headings the note gets contents with search, and sections can be collapsed. Pin, archive and delete (with “Undo”) are on the bar on top.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Notes',
            description: 'A place for what you want to keep: codes, lists, ideas, songs with links, books to read. No deadlines — things for a given day are tasks in the Planner.',
        },
        format: {
            title: 'Writing a note',
            description: 'Open a note and click the text. Add headings, lists and checkboxes from the bar above the content; a link pasted on an empty line becomes a button with the service icon. A heading with a checkbox is a section to tick off, e.g. a book read.',
        },
        end: {
            title: 'That’s it',
            description: 'The guide is always under the “Help” button — First steps are there too. And “What’s this?” next to a section explains it without the guide.',
        },
    },
    firstSteps: {
        note: {
            title: 'First note',
            action: 'Jot something down',
            what: 'Type something in the “Jot something down…” field and press Enter — a gate code, a gift idea.',
            why: 'It’s the quickest way: one line and done, and you come back to it whenever you want.',
        },
        category: {
            title: 'A category',
            action: 'Add a category',
            what: 'Create a category, e.g. Music or Books, and put notes in it.',
            why: 'Instead of one long list you see just what you’re looking for.',
        },
        structure: {
            title: 'A note with headings or checkboxes',
            action: 'Start a note',
            what: 'Write a note with headings (“## ”) or checkboxes (“- [ ] ”) — e.g. books to read.',
            why: 'Headings with a checkbox tick off like sections, and a long note gets contents with search.',
        },
        pinned: {
            title: 'A pinned note',
            action: 'Pin a note',
            what: 'Open a note you often come back to and click the pin on the bar.',
            why: 'Pinned notes are always at the top of the list.',
        },
    },
    links: {
        defaultLabel: 'Link',
        tabs: 'Tabs',
        lyrics: 'Lyrics',
        map: 'Map',
    },
};
